// Phase 1G + Real customer-facing OAuth -- Connections: no API keys, no
// client secrets, no tokens, ever. Each card says WHAT MARTIAN GETS ->
// WHAT THAT UNLOCKS, then a real "Connect X" button that sends the
// founder's browser to the real provider's own consent screen and back.
//
// Providers with a real, working OAuth app configured connect for real
// (Shopify, Meta Ads, Google Search Console, Google Analytics, Google
// Ads, and every social platform via Martian's own social-connections
// abstraction). A provider whose platform-level OAuth app isn't
// registered yet honestly says "Not set up yet" instead of pretending to
// work -- never a fabricated success.
//
// "Zernio" -- the infrastructure behind the social platforms -- is never
// named anywhere on this page or in anything it calls.
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { oauthAPI, socialConnectionsAPI } from '@/lib/api';
import { connectorsAPI } from '@/lib/intelligenceApi';
import { connectionTruth } from '@/lib/connectionTruthState';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, Eyebrow, Loading, PageHeader, PrimaryButton, SecondaryButton, TextButton, formatDate } from '../ui';

// A real status from the shared connection state machine (backend:
// connectors/registry.py's CONNECTOR_STATUSES / social_connections.py's
// own equivalent) -> how it reads to a founder. "Connected" alone is
// deliberately never shown as "Healthy" -- OAuth succeeding only ever
// means credentials were obtained (Section: "'Healthy' requires a
// successful authenticated provider/API health check").
const STATUS_CHIP = {
  NOT_CONNECTED: { label: 'Not connected', tone: 'neutral' },
  CONNECTED: { label: 'Connected', tone: 'good' },
  SYNCING: { label: 'Syncing…', tone: 'neutral' },
  HEALTHY: { label: 'Connected', tone: 'good' },
  ACTION_REQUIRED: { label: 'Needs attention', tone: 'warn' },
  EXPIRED: { label: 'Expired — reconnect', tone: 'warn' },
  DISCONNECTED: { label: 'Not connected', tone: 'neutral' },
  ERROR: { label: "Couldn't verify", tone: 'bad' },
  CREDENTIAL_REVOKED: { label: 'Not connected', tone: 'neutral' },
};

const ERROR_MESSAGES = {
  provider_denied: "You didn't finish connecting — no changes were made.",
  missing_state: 'That connection attempt could not be verified. Please try again.',
  state_invalid: 'That connection attempt could not be verified. Please try again.',
  state_expired: 'That took a bit long — please try connecting again.',
  state_already_used: 'That connection link was already used. Please try connecting again.',
  state_provider_mismatch: 'Something about that connection didn’t match. Please try again.',
  invalid_signature: "That didn't look like it came from the provider. Nothing was connected.",
  invalid_shop_domain: "That doesn't look like a real store address.",
  shop_mismatch: "That store didn't match the one you asked to connect. Nothing was connected.",
  no_refresh_token: 'Google needs you to approve access again — please try connecting once more.',
  invalid_grant: "That didn't work — please try connecting again.",
  not_configured: "This connection isn't set up yet. Ask a Martian administrator.",
};
function errorMessage(code) {
  return ERROR_MESSAGES[code] || "Couldn't connect — please try again.";
}

// providers backed by the read-only connector framework (connectorsAPI) --
// each needs a real, working OAuth app before "Connect" does anything.
const DATA_CONNECTIONS = [
  {
    key: 'google_search_console', provider: 'google_search_console', name: 'Google Search Console',
    gets: ['What people search for', 'Impressions and clicks', 'Organic demand'],
    unlocks: ['Real customer questions for your watchlist', 'Search-demand context for every decision', 'Measurement against search'],
    next: true,
  },
  {
    key: 'google_analytics', provider: 'google_analytics', name: 'Google Analytics',
    gets: ['Sessions and landing pages', 'Engagement', 'Conversion context'],
    unlocks: ['Whether approved work changed what visitors do'],
  },
  {
    key: 'shopify', provider: 'shopify', name: 'Shopify', needsShop: true,
    gets: ['Products', 'Orders', 'Revenue'],
    unlocks: ['Whether growth work produced sales'],
  },
  {
    key: 'meta_marketing', provider: 'meta_marketing', name: 'Meta Ads',
    gets: ['Campaigns, spend and results'],
    unlocks: ['Paid-social decisions grounded in your real numbers'],
  },
  {
    key: 'google_ads', provider: 'google_ads', name: 'Google Ads',
    gets: ['Campaigns, spend and conversions'],
    unlocks: ['Paid-search decisions grounded in your real numbers'],
  },
];

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram' }, { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' }, { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' }, { key: 'x', label: 'X' },
  { key: 'pinterest', label: 'Pinterest' }, { key: 'reddit', label: 'Reddit' },
  { key: 'threads', label: 'Threads' },
];

function connectorStatusOf(connectors, provider) {
  const match = (connectors || []).find((c) => c.provider === provider);
  if (!match) return { status: 'NOT_CONNECTED' };
  return match;
}

// Every status EXCEPT "no credential on file" -- these are the ones a
// founder can disconnect/health-check; everything else still needs a
// first "Connect".
const INACTIVE_STATUSES = new Set(['NOT_CONNECTED', 'DISCONNECTED', 'CREDENTIAL_REVOKED', undefined]);
const isActiveConnection = (status) => !INACTIVE_STATUSES.has(status);

// ── the shop-domain prompt (Shopify only) ──
function ShopDomainDialog({ open, onOpenChange, onSubmit, busy }) {
  const [shop, setShop] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shopify-shop-dialog">
        <DialogHeader>
          <DialogTitle>Connect Shopify</DialogTitle>
          <DialogDescription>What's your store's address?</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-1.5">
          <Label>Store address</Label>
          <Input autoFocus data-testid="shopify-shop-input" placeholder="mystore.myshopify.com" value={shop} onChange={(e) => setShop(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && shop.trim() && onSubmit(shop.trim())} />
        </div>
        <DialogFooter>
          <SecondaryButton onClick={() => onOpenChange(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={() => onSubmit(shop.trim())} disabled={busy || !shop.trim()} testId="shopify-shop-continue">Continue to Shopify</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── the account/property selection step (Meta, Google Ads/GSC/GA4) ──
function SelectionDialog({ open, provider, options, loading, onChoose, busy }) {
  const [value, setValue] = useState('');
  const [typedId, setTypedId] = useState('');
  useEffect(() => { setValue(''); setTypedId(''); }, [provider]);
  const label = { meta_marketing: 'ad account', google_ads: 'Google Ads account', google_search_console: 'property', google_analytics: 'property' }[provider] || 'account';
  const chosen = value || typedId.trim();
  return (
    <Dialog open={open}>
      <DialogContent data-testid="oauth-selection-dialog"
        onInteractionOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Which {label}?</DialogTitle>
          <DialogDescription>Martian will only read data for the one you choose.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          {loading ? <Loading label="Loading your accounts…" /> : options.length === 0 ? (
            <p className="text-[13.5px] text-[var(--text-secondary)]">No accessible accounts were found for that sign-in.</p>
          ) : (
            <Select value={value} onValueChange={(next) => { setValue(next); setTypedId(''); }}>
              <SelectTrigger data-testid="oauth-selection-trigger"><SelectValue placeholder={`Choose a ${label}`} /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {provider === 'google_analytics' && !loading ? (
            <div className="space-y-1.5">
              <Label>Or enter a property ID</Label>
              <Input data-testid="oauth-ga4-property-id" placeholder="123456789" value={typedId}
                onChange={(e) => { setTypedId(e.target.value); setValue(''); }} />
              <p className="text-[12px] text-[var(--text-tertiary)]">Martian verifies access with a read-only Analytics request before connecting.</p>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <PrimaryButton onClick={() => onChoose(chosen)} disabled={busy || !chosen} testId="oauth-selection-confirm">Connect</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectionRow({ name, gets, unlocks, status, truth, error, lastSync, next, primaryAction, secondaryActions, testId }) {
  const chip = truth || STATUS_CHIP[status] || STATUS_CHIP.NOT_CONNECTED;
  return (
    <li className="py-6 border-t border-[var(--border)] grid md:grid-cols-[220px_1fr_auto] gap-3 md:gap-6 items-start" data-testid={testId}>
      <div>
        <p className="text-[16px] font-semibold">{name}</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <Chip tone={chip.tone}>{chip.label}</Chip>
          {next && status === 'NOT_CONNECTED' && <span className="text-[12px] text-[var(--accent)] font-medium">Unlocks the most next</span>}
        </div>
        {error && <p className="mt-1.5 text-[12px] text-[var(--martian-coral,#F06A4F)] max-w-[26ch]">{error}</p>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-[13.5px]">
        <div><Eyebrow>Martian sees</Eyebrow><ul className="mt-1.5 space-y-1">{gets.map((g) => <li key={g}>{g}</li>)}</ul></div>
        <div><Eyebrow>That unlocks</Eyebrow><ul className="mt-1.5 space-y-1 text-[var(--text-secondary)]">{unlocks.map((u) => <li key={u}>{u}</li>)}</ul></div>
        {lastSync && <p className="sm:col-span-2 text-[12.5px] text-[var(--text-tertiary)]">Last checked {formatDate(lastSync, true)}</p>}
      </div>
      <div className="md:pt-1 flex flex-col items-start md:items-end gap-2">
        {primaryAction}
        {secondaryActions}
      </div>
    </li>
  );
}

export default function ConnectionsPage() {
  const { workspaceId, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const [params, setParams] = useSearchParams();
  const [connectors, setConnectors] = useState(null);
  const [social, setSocial] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [shopDialogOpen, setShopDialogOpen] = useState(false);
  const [selection, setSelection] = useState(null); // { provider, pendingId, options, loading }

  const loadConnectors = useCallback(() => {
    if (!workspaceId) return;
    connectorsAPI.list(workspaceId).then((r) => setConnectors(r.data?.connectors || r.data || [])).catch(() => setConnectors([]));
  }, [workspaceId]);
  const loadSocial = useCallback(() => {
    if (!workspaceId) return;
    socialConnectionsAPI.list(workspaceId).then((r) => setSocial(r.data?.connections || [])).catch(() => setSocial([]));
  }, [workspaceId]);
  useEffect(() => { setConnectors(null); loadConnectors(); }, [loadConnectors]);
  useEffect(() => { setSocial(null); loadSocial(); }, [loadSocial]);

  // ── handle the return from a provider's consent screen ──
  useEffect(() => {
    const provider = params.get('provider');
    if (!provider) return;
    const clear = () => setParams({}, { replace: true });
    if (params.get('error')) {
      toast.error(errorMessage(params.get('error')));
      clear();
    } else if (params.get('connected')) {
      if (params.get('verified') === '0') toast("Connected, but Martian couldn't read your data yet — use Check connection.");
      else toast.success('Connected');
      loadConnectors(); loadSocial();
      clear();
    } else if (params.get('connecting')) {
      toast('Still connecting — this can take a moment.');
      loadSocial();
      clear();
    } else if (params.get('select') && params.get('pending_id') && workspaceId) {
      const pendingId = params.get('pending_id');
      setSelection({ provider, pendingId, options: [], loading: true });
      oauthAPI.options(provider, workspaceId, pendingId)
        .then((r) => setSelection((s) => (s ? { ...s, options: r.data?.options || [], loading: false } : s)))
        .catch(() => { toast.error("Couldn't load your accounts — please try connecting again."); setSelection(null); });
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, workspaceId]);

  const startOAuth = async (provider, extra = {}) => {
    setBusyKey(provider);
    try {
      const res = await oauthAPI.start(provider, workspaceId, extra);
      window.location.assign(res.data.authorize_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start that connection");
      setBusyKey(null);
    }
  };

  const finalizeSelection = async (accountReference) => {
    if (!selection) return;
    setBusyKey('selection');
    try {
      const res = await oauthAPI.finalize(selection.provider, workspaceId, selection.pendingId, accountReference);
      const finalStatus = res?.data?.status;
      if (finalStatus === 'HEALTHY') toast.success('Connected and verified');
      else if (finalStatus === 'CONNECTED') toast.success('Connected');
      else toast("Connected, but Martian couldn't read your data yet — use Check connection.");
      setSelection(null);
      loadConnectors();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't finish connecting");
    } finally {
      setBusyKey(null);
    }
  };

  const healthCheck = async (connectorId, key) => {
    setBusyKey(key);
    try {
      const res = await connectorsAPI.healthCheck(workspaceId, connectorId);
      if (res.data?.health?.status === 'OK') toast.success('Connection is healthy'); else toast.error(res.data?.health?.error_message_safe || "Couldn't verify this connection");
      loadConnectors();
    } catch { toast.error("Couldn't check this connection"); } finally { setBusyKey(null); }
  };
  const disconnectConnector = async (connectorId, key) => {
    setBusyKey(key);
    try { await connectorsAPI.disconnect(workspaceId, connectorId); toast.success('Disconnected'); loadConnectors(); }
    catch { toast.error("Couldn't disconnect"); } finally { setBusyKey(null); }
  };

  const connectSocial = async (platform) => {
    setBusyKey(`social-${platform}`);
    try {
      const res = await socialConnectionsAPI.connect(workspaceId, platform);
      window.location.assign(res.data.authorize_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start that connection");
      setBusyKey(null);
    }
  };
  const socialHealthCheck = async (platform) => {
    setBusyKey(`social-${platform}`);
    try {
      const res = await socialConnectionsAPI.healthCheck(workspaceId, platform);
      if (res.data?.health?.status === 'OK') toast.success('Connection is healthy'); else toast.error(res.data?.health?.error_message_safe || "Couldn't verify this connection");
      loadSocial();
    } catch { toast.error("Couldn't check this connection"); } finally { setBusyKey(null); }
  };
  const socialDisconnect = async (platform) => {
    setBusyKey(`social-${platform}`);
    try { await socialConnectionsAPI.disconnect(workspaceId, platform); toast.success('Disconnected'); loadSocial(); }
    catch { toast.error("Couldn't disconnect"); } finally { setBusyKey(null); }
  };

  const loading = loadingClients || (workspaceId && (connectors === null || social === null));
  if (loading) return <Loading />;

  return (
    <div data-testid="connections-page">
      <PageHeader
        eyebrow="Evidence · Connections"
        title="What Martian can see"
        summary="Each connection gives Martian real data about your business and unlocks better decisions. Martian never asks you for passwords or technical credentials."
        secondary={<TextButton to={fp('/evidence')}>Back to Evidence</TextButton>}
      />

      <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="connection-ai-search">
        <div>
          <p className="text-[16px] font-semibold">AI search checks</p>
          <p className="mt-1 text-[13.5px] text-[var(--text-secondary)]">Built in. Martian asks AI assistants your customers' questions and records every answer and source.</p>
        </div>
        <Chip tone="good">Always on</Chip>
      </div>

      <ul>
        {DATA_CONNECTIONS.map((c) => {
          const record = connectorStatusOf(connectors, c.provider);
          const key = c.key;
          return (
            <ConnectionRow key={key} testId={`connection-${key}`} name={c.name} gets={c.gets} unlocks={c.unlocks} next={c.next}
              status={record.status || 'NOT_CONNECTED'} truth={connectionTruth(record)} error={record.error_message_safe} lastSync={record.last_successful_sync_at || record.last_sync_at || record.last_health_check_at}
              primaryAction={
                isActiveConnection(record.status) ? (
                  <SecondaryButton onClick={() => disconnectConnector(record.connector_id, key)} disabled={busyKey === key} testId={`connection-${key}-disconnect`}>Disconnect</SecondaryButton>
                ) : (
                  <PrimaryButton
                    onClick={() => (c.needsShop ? setShopDialogOpen(true) : startOAuth(c.provider))}
                    disabled={busyKey === c.provider} testId={`connection-${key}-action`}>
                    {busyKey === c.provider ? 'Connecting…' : `Connect ${c.name}`}
                  </PrimaryButton>
                )
              }
              secondaryActions={isActiveConnection(record.status) && (
                <TextButton onClick={() => healthCheck(record.connector_id, key)} disabled={busyKey === key} testId={`connection-${key}-check`}>Check connection</TextButton>
              )}
            />
          );
        })}
      </ul>

      <div className="pt-8 mt-2 border-t border-[var(--border)]">
        <Eyebrow>Social</Eyebrow>
        <p className="mt-1.5 text-[13.5px] text-[var(--text-secondary)] max-w-[62ch]">Connect the channels you post on. Martian sees your own posts and their engagement — never messages, never followers' personal data.</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-8">
          {SOCIAL_PLATFORMS.map((p) => {
            const conn = (social || []).find((s) => s.platform === p.key) || { status: 'NOT_CONNECTED' };
            const chip = STATUS_CHIP[conn.status] || STATUS_CHIP.NOT_CONNECTED;
            const isConnected = !['NOT_CONNECTED', 'DISCONNECTED'].includes(conn.status);
            return (
              <li key={p.key} className="py-3.5 border-t border-[var(--border-light,var(--border))] first:border-t-0 flex items-center justify-between gap-3" data-testid={`social-${p.key}`}>
                <div className="min-w-0">
                  <p className="text-[14.5px] font-medium">{p.label}{conn.account_handle ? <span className="text-[var(--text-tertiary)] font-normal"> · {conn.account_handle}</span> : null}</p>
                  <Chip tone={chip.tone}>{chip.label}</Chip>
                </div>
                {isConnected ? (
                  <div className="flex items-center gap-3 shrink-0">
                    <TextButton onClick={() => socialHealthCheck(p.key)} disabled={busyKey === `social-${p.key}`}>Check</TextButton>
                    <TextButton onClick={() => socialDisconnect(p.key)} disabled={busyKey === `social-${p.key}`} tone="danger">Disconnect</TextButton>
                  </div>
                ) : (
                  <SecondaryButton onClick={() => connectSocial(p.key)} disabled={busyKey === `social-${p.key}`} testId={`social-${p.key}-connect`}>
                    {busyKey === `social-${p.key}` ? 'Connecting…' : 'Connect'}
                  </SecondaryButton>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <p className="mt-8 text-[12.5px] text-[var(--text-tertiary)] max-w-[70ch]" data-testid="connections-honesty-note">
        Google Business Profile is coming soon. Everything else above connects with your real sign-in — Martian never asks you to paste a key or token.
      </p>

      <ShopDomainDialog open={shopDialogOpen} onOpenChange={setShopDialogOpen} busy={busyKey === 'shopify'}
        onSubmit={(shop) => { setShopDialogOpen(false); startOAuth('shopify', { shop }); }} />

      <SelectionDialog open={!!selection} provider={selection?.provider} options={selection?.options || []} loading={!!selection?.loading}
        busy={busyKey === 'selection'} onChoose={finalizeSelection} />
    </div>
  );
}
